import { Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { createSocialProof, updateSocialProof } from "../api/company-knowledge";
import { useLoading, useToaster } from "../hooks";
import {
  BusinessFeature,
  CustomerProfileSubset,
  MessagingPillar,
  SocialProof,
} from "../types/company-knowledge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import { toaster } from "./toaster";

// A proof passes `proof` to edit an existing one, or null to create.
// The subsets, features, and pillars come from the parent page, which
// loads them alongside the proofs for the checkbox groups.

const socialProofSchema = z.object({
  title: z.string().trim().min(1, "A title is required."),
  customer: z.string(),
  narrative: z.string(),
  evidence: z.string(),
  "source-url": z
    .string()
    .refine(
      (value) => value === "" || /^https?:\/\//.test(value),
      "Enter a valid URL (starting with http:// or https://), or leave it blank.",
    ),
  "subset-ids": z.array(z.number()),
  "related-feature-ids": z.array(z.number()),
  "related-pillar-ids": z.array(z.number()),
});

type SocialProofFormData = z.infer<typeof socialProofSchema>;

interface SocialProofDialogProps {
  features: BusinessFeature[];
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  pillars: MessagingPillar[];
  proof?: SocialProof | null;
  subsets: CustomerProfileSubset[];
  workspaceSlug: string;
}

export function SocialProofDialog({
  features,
  isOpen,
  onOpenChange,
  onSuccess,
  pillars,
  proof,
  subsets,
  workspaceSlug,
}: SocialProofDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<SocialProofFormData>({
    resolver: zodResolver(socialProofSchema),
    defaultValues: emptyProof,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    reset(proof ? toFormData(proof) : emptyProof);
  }, [isOpen, proof, reset]);

  const onSubmit = async (data: SocialProofFormData) => {
    await withLoading(async () => {
      await withToaster(async () => {
        if (proof) {
          await updateSocialProof(workspaceSlug, proof.id, data);
          toaster.create({ type: "success", title: "Social proof updated" });
        } else {
          await createSocialProof(workspaceSlug, data);
          toaster.create({ type: "success", title: "Social proof created" });
        }
        onOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {proof ? "Edit Social Proof" : "Create Social Proof"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody paddingBottom="6">
            <Stack gap="4">
              <Field
                label="Title"
                invalid={!!errors.title}
                errorText={errors.title?.message}
              >
                <Input
                  size="sm"
                  placeholder="The case study's or testimonial's title"
                  {...register("title")}
                />
              </Field>
              <Field
                label="Customer or person"
                invalid={!!errors.customer}
                errorText={errors.customer?.message}
              >
                <Input
                  {...register("customer")}
                  placeholder="Who this proof is about"
                />
              </Field>
              <Field
                label="Case study or testimonial"
                invalid={!!errors.narrative}
                errorText={errors.narrative?.message}
              >
                <Textarea
                  {...register("narrative")}
                  placeholder="The story, in the customer's words where possible."
                  rows={3}
                />
              </Field>
              <Field
                label="Supported results or exact quote"
                helperText="Only verifiable numbers or verbatim quotes; ad copy may cite these."
                invalid={!!errors.evidence}
                errorText={errors.evidence?.message}
              >
                <Textarea
                  {...register("evidence")}
                  placeholder="The numbers, or the quote, exactly."
                  rows={2}
                />
              </Field>
              <Field
                label="Supporting source"
                invalid={!!errors["source-url"]}
                errorText={errors["source-url"]?.message}
              >
                <Input
                  {...register("source-url")}
                  placeholder="https://example.com/case-study"
                />
              </Field>
              {/* The KnowledgeCheckboxGroup helper at the bottom of
                  this file avoids <Field> deliberately: a Chakra Field
                  shares its label/input ids with every control inside
                  it, which makes each checkbox's label point at the
                  first checkbox's input. */}
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Applies to all subsets when none are selected."
                items={subsets}
                label="Applicable ICP subsets"
                name="subset-ids"
              />
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Optional."
                items={features}
                label="Related business features"
                name="related-feature-ids"
              />
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Optional."
                items={pillars}
                label="Related messaging pillars"
                name="related-pillar-ids"
              />
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange({ open: false })}
            >
              Cancel
            </Button>
            <Button loading={isLoading} type="submit">
              {proof ? "Save Changes" : "Create Social Proof"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

// File-local helper for the id-array checkbox groups. Field is
// deliberately avoided: a Chakra Field shares its label/input ids
// with every control inside it, which makes each checkbox's label
// point at the first checkbox's input.
interface KnowledgeCheckboxGroupProps {
  control: Control<SocialProofFormData>;
  helperText: string;
  items: { id: number; name: string }[];
  label: string;
  name: "subset-ids" | "related-feature-ids" | "related-pillar-ids";
}

function KnowledgeCheckboxGroup({
  control,
  helperText,
  items,
  label,
  name,
}: KnowledgeCheckboxGroupProps) {
  return (
    <Stack gap="1">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Stack gap="2">
            {items.map((item) => {
              const checked = field.value.includes(item.id);
              return (
                <Checkbox
                  key={item.id}
                  checked={checked}
                  onCheckedChange={(details) => {
                    const next = details.checked
                      ? [...field.value, item.id]
                      : field.value.filter((id) => id !== item.id);
                    field.onChange(next);
                  }}
                >
                  {item.name}
                </Checkbox>
              );
            })}
            <Text color="fg.muted" fontSize="sm" fontStyle="italic">
              {helperText}
            </Text>
          </Stack>
        )}
      />
    </Stack>
  );
}

const emptyProof: SocialProofFormData = {
  title: "",
  customer: "",
  narrative: "",
  evidence: "",
  "source-url": "",
  "subset-ids": [],
  "related-feature-ids": [],
  "related-pillar-ids": [],
};

// The schema's nullable fields arrive as null over the wire; empty
// strings are the form's representation of "unset".
const toFormData = (proof: SocialProof): SocialProofFormData => ({
  title: proof.title ?? "",
  customer: proof.customer ?? "",
  narrative: proof.narrative ?? "",
  evidence: proof.evidence ?? "",
  "source-url": proof["source-url"] ?? "",
  "subset-ids": proof["subset-ids"] ?? [],
  "related-feature-ids": proof["related-feature-ids"] ?? [],
  "related-pillar-ids": proof["related-pillar-ids"] ?? [],
});
